import { ArrowDropDown } from "@mui/icons-material";
import { Box, Collapse, IconButton, Paper } from "@mui/material";
import { Button, Typography } from "@procore/core-react";
import { forwardRef, useLayoutEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { selectIsOnline } from "slices/explorer";

import { getCoachmarkTop } from "./coachmarkLayout";

export interface CoachmarkProps {
    label: string;
    description?: string;
    buttonLabel?: string;
    onButtonClick?: () => void;
    visible: boolean;
    topOffset?: string;
}

export const Coachmark = forwardRef<HTMLDivElement, CoachmarkProps>(function Coachmark(
    { label, description, buttonLabel, onButtonClick, visible, topOffset },
    ref,
) {
    const [expanded, setExpanded] = useState(false);
    const hasExpandableContent = Boolean(description || (buttonLabel && onButtonClick));
    const isOnline = useAppSelector(selectIsOnline);

    const paperRef = useRef<HTMLDivElement | null>(null);
    const [naturalWidth, setNaturalWidth] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (paperRef.current && naturalWidth === null) {
            setNaturalWidth(paperRef.current.offsetWidth);
        }
    }, [naturalWidth]);

    return (
        <Paper
            ref={(element: HTMLDivElement | null) => {
                paperRef.current = element;
                if (typeof ref === "function") {
                    ref(element);
                } else if (ref) {
                    ref.current = element;
                }
            }}
            sx={{
                position: "absolute",
                top: getCoachmarkTop(topOffset, isOnline),
                left: "50%",
                translate: "-50% 0",
                borderRadius: "4px",
                padding: "4px",
                background: "rgba(0, 0, 0, 0.8)",
                color: "white",
                overflow: "hidden",
                transition: "opacity 0.2s, width 0.2s, top 0.2s",
                pointerEvents: visible ? "auto" : "none",
                opacity: visible ? 1 : 0,
                zIndex: 1052,
                display: "flex",
                gap: "0.5rem",
                width:
                    hasExpandableContent && naturalWidth !== null
                        ? expanded
                            ? "240px"
                            : `${naturalWidth}px`
                        : undefined,
                maxWidth: "240px",
                letterSpacing: "0.25px",
            }}
        >
            {hasExpandableContent && (
                <IconButton
                    sx={{
                        borderRadius: "4px",
                        background: "#3E3E3E",
                        color: "white",
                        alignSelf: "start",
                        "&:hover": { background: "#3E3E3E" },
                        height: "24px",
                        width: "24px",
                    }}
                    onClick={() => setExpanded((prev) => !prev)}
                    size="small"
                >
                    <ArrowDropDown sx={{ rotate: expanded ? "180deg" : "0deg", transition: "rotate 0.2s" }} />
                </IconButton>
            )}
            <Box sx={{ gap: "2px", display: "flex", flexDirection: "column", flex: 1 }}>
                <Typography
                    weight="semibold"
                    color="white"
                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                    {label}
                </Typography>
                {hasExpandableContent && (
                    <Collapse in={expanded} timeout={200} unmountOnExit>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: "200px" }}>
                            {description && <Typography color="white">{description}</Typography>}
                            {buttonLabel && onButtonClick && (
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    onClick={onButtonClick}
                                    style={{
                                        background: "#3E3E3E",
                                        color: "white",
                                        alignSelf: "flex-start",
                                        minWidth: "auto",
                                    }}
                                >
                                    {buttonLabel}
                                </Button>
                            )}
                        </Box>
                    </Collapse>
                )}
            </Box>
        </Paper>
    );
});
